// Grid / Tabular visual — rendered with ProPivot in FLAT mode: every visible
// column is placed in the pivot's row axis with `grid.type = 'flat'` and no
// measures, so ProPivot renders a plain, sortable table. This replaces the old
// ag-grid path (dropped from visual-engine). Column order, widths, per-column
// display formats and ADV_FORMAT colouring all come from the ColumnGroups and
// are handled inside PivotVisual's grid report builder. Until ProPivot loads (or
// if it fails) a dependency-free HTML table (FallbackTable) renders the same data
// so the grid is never blank.

import { useMemo } from 'react'
import type { VisualJsonData, VisualLayout } from './types'
import { humanizeColumnLabel, toMappedColumn, type MappedColumn } from './dataUtils'
import { formatCell } from './formatters'
import { parseAdvFormat, conditionalStyle } from './conditionalFormat'
import type { NativeRendererProps, SelectHandler } from './nativeRenderers'
import { PivotVisual } from './PivotVisual'
import { DisplayCell, toColDef, hasRichDisplay } from './displayCell'
import { useRowSelection, SELECT_BG, SELECT_RING_INSET } from './selection'

function resolveColumns(jsonData: VisualJsonData, visualLayout: VisualLayout): MappedColumn[] {
  const schema = (jsonData?.[0] ?? {}) as Record<string, { type?: string }>
  const cols: MappedColumn[] = []
  const seen = new Set<string>()
  for (const group of visualLayout?.ColumnGroups ?? []) {
    const columns = (group as { COLUMNS?: Array<Record<string, unknown>> }).COLUMNS ?? []
    for (const c of columns) {
      const name = (c.NAME ?? c.name) as string | undefined
      if (!name || seen.has(name)) continue
      seen.add(name)
      const mc = toMappedColumn(c, name, schema)
      if (!mc.hidden) cols.push(mc)
    }
  }
  if (cols.length === 0) {
    for (const k of Object.keys(schema)) {
      if (k === 'id') continue
      cols.push({ name: k, label: humanizeColumnLabel(k), type: schema[k]?.type })
    }
  }
  return cols
}

const isNumeric = (c: MappedColumn) => c.coltype === 'NUMBER' || c.type === 'number'

export function GridTable({ jsonData, visualLayout, optionsArr, onSelect, onRowClick, autoHeight }: NativeRendererProps) {
  const cols = useMemo(() => resolveColumns(jsonData, visualLayout), [jsonData, visualLayout])
  const rows = useMemo(() => (jsonData?.slice(1) ?? []) as Array<Record<string, unknown>>, [jsonData])
  // `onRowClick` is opt-in and rides the same row-click path as `onSelect`;
  // with neither set the grid is display-only, exactly as before.
  const rowHandler = useMemo<SelectHandler | undefined>(() => {
    if (!onRowClick) return onSelect
    if (!onSelect) return onRowClick
    return (row) => { onSelect(row); onRowClick(row) }
  }, [onSelect, onRowClick])

  if (cols.length === 0) {
    return <div style={msgStyle}>No columns to display.</div>
  }

  // ProPivot (flat) renders the real grid; the FallbackTable stands in while it
  // lazy-loads or if it fails.
  return (
    <PivotVisual
      jsonData={jsonData}
      visualLayout={visualLayout}
      optionsArr={optionsArr}
      onSelect={rowHandler}
      variant="grid"
      autoHeight={autoHeight}
      renderFallback={() => <FallbackTable cols={cols} rows={rows} onSelect={rowHandler} keyboard={!!onRowClick} />}
    />
  )
}

// Plain-HTML fallback (loading / load failure): same data, per-column display
// formats and ADV_FORMAT conditional styles applied so it matches the pivot grid.
function FallbackTable({ cols, rows, onSelect, keyboard }: {
  cols: MappedColumn[]; rows: Array<Record<string, unknown>>; onSelect?: SelectHandler
  /** Rows are focusable and Enter picks one (set with `onRowClick`). */
  keyboard?: boolean
}) {
  const { select, isSelected } = useRowSelection(onSelect)
  const colDefs = cols.map((c) => toColDef(c))
  return (
    <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c.name}
                style={{
                  position: 'sticky', top: 0, textAlign: isNumeric(c) ? 'right' : 'left',
                  padding: '8px 12px', borderBottom: '2px solid var(--pg-border, #e5e7eb)',
                  background: 'var(--pg-surface, var(--color-bg-secondary, #f8f9fa))',
                  color: 'var(--pg-heading, var(--color-text, #111))', fontWeight: 600, whiteSpace: 'nowrap',
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const sel = isSelected(row)
            return (
            <tr
              key={i}
              onClick={select ? () => select(row) : undefined}
              tabIndex={keyboard && select ? 0 : undefined}
              onKeyDown={keyboard && select ? (e) => { if (e.key === 'Enter') { e.preventDefault(); select(row) } } : undefined}
              aria-selected={select ? sel : undefined}
              style={{
                background: sel ? SELECT_BG : i % 2 ? 'var(--pg-surface-alt, rgba(127,127,127,0.04))' : 'transparent',
                boxShadow: sel ? SELECT_RING_INSET : undefined,
                cursor: select ? 'pointer' : undefined,
              }}
            >
              {cols.map((c, ci) => {
                const advStyle = c.advFormat ? conditionalStyle(parseAdvFormat(c.advFormat), row[c.name]) || {} : {}
                return (
                <td
                  key={c.name}
                  style={{
                    padding: '7px 12px', borderBottom: '1px solid var(--pg-border, #eee)',
                    textAlign: isNumeric(c) ? 'right' : 'left', color: 'var(--pg-text, var(--color-text, #222))',
                    fontVariantNumeric: isNumeric(c) ? 'tabular-nums' : undefined, whiteSpace: 'nowrap',
                    ...advStyle,
                  }}
                >
                  {hasRichDisplay(c)
                    ? <DisplayCell value={row[c.name]} col={colDefs[ci]} row={row} columns={colDefs} />
                    : formatCell(row[c.name], { type: c.type, coltype: c.coltype, format: c.format, pattern: c.pattern })}
                </td>
                )
              })}
            </tr>
            )
          })}
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} style={msgStyle}>No rows.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

const msgStyle = { padding: 16, fontSize: 13, color: 'var(--pg-muted, #888)', textAlign: 'center' as const }

export default GridTable
